import React, { Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { MdChevronLeft, MdChevronRight, MdOutlineFolder, MdRateReview, MdArrowBack, MdArrowForward, MdPlayCircleOutline, MdPauseCircleOutline, MdFilterList, MdCancel } from "react-icons/md"
import CircularProgress from "@mui/material/CircularProgress"
// @ts-ignore - ModernMUITable.js doesn't have TypeScript declarations
import ModernMUITable from "../../../Components/ModernMUITable"
import { MobergBoxShadow, MobergColumn, MobergFontSize } from "../../../Moberg"
import { useRecoilState, useRecoilValue } from "recoil"
import { annotationGroupsAtom, activeAnnotationSessionAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/Annotations"
import { currentPatientFileInfoAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile"
import { useModalProvider } from "../../../Providers/ModalProvider"
import { AnnotationSessionManagerModal } from "../ToolBar/Modals/AnnotationSessionManagerModal"
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal"
import { AnnotationGroupLoader } from "../../../Pages/Data/Visualize/DataReview/Components/Modals/AnnotationGroupLoader/AnnotationGroupLoader"
import {
	MobergAnimationTiming,
	MobergAnimationCurve,
	MobergDropdown,
	MobergRow,
	MobergButtonShape,
	MobergIconSize,
	MobergDropdownSize,
	MobergButton,
	MobergTheme,
	getColors,
} from "../../../Moberg"
import { MdOutlineErrorOutline } from "react-icons/md"
import { useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider"
import { usePlayThroughAnnotation } from "../../../Providers/usePlayThroughAnnotation"
import { EventReviewFormsWindow } from "../../../Pages/Data/Visualize/DataReview/Components/Visualizations/EventReview/React/Forms/EventReviewForm"
import { DurationUnit, fromMilliseconds, getFormattedDuration } from "../../../Pages/Data/Visualize/DataReview/Hooks/useDuration"

import { jumpAllWindows } from "../../../Pages/Data/Visualize/DataReview/Atoms/Visualizations"
import { Annotation } from "../Variables/Annotations"
import { useEventReview, useEventReviewForm } from "../../../Providers/EventReviewProvider"
import { MobergDropdownOption } from "../../../Components/MobergDropdown/MobergDropdown"
import { AsyncTask } from "../../../Hooks/useAsyncTask"
import { MobergButtonVariant } from "../../../Components/MobergButton/MobergButton"
import { useEventReviewHotKeys } from "../../../Providers/useEventReviewHotKeys"
import { formatDate } from "../../../Pages/Data/Visualize/DataReview/utils"
import { useMobergMenu } from "../../../Hooks/useMobergMenu"
import { EventReviewConfig } from "../../../Pages/Data/Visualize/DataReview/Atoms/EventReview"
import { SetterOrUpdater } from "recoil"

interface AnnotationsBarProps {
	enableSidebar: boolean
	setEnableSidebar: Dispatch<SetStateAction<boolean>>
}

interface AnnotationTableRow {
	id: number
	name: string
	time: number
	duration: number
	complete: boolean
	isCurrent: boolean
}

interface AnnotationTableColumn {
	field: string
	width: number
	headerName: string
	visible: boolean
	minWidth: number
	maxWidth: number
	renderCell: (params: any) => React.JSX.Element
}

interface PaginationModel {
	page: number
	pageSize: number
}

type TableSortModel = Array<{ field: string; sort: "asc" | "desc" | null | undefined }>

const ICON_BUTTON_PADDING = "2px"
const TEXT_BUTTON_PADDING = "2px 8px"
const STATUS_COLORS = {
	complete: {
		background: "rgba(76, 175, 80, 0.3)",
		text: "#4CAF50",
	},
	incomplete: {
		background: "rgba(244, 67, 54, 0.3)",
		text: "#F44336",
	},
}
const CURRENT_ANNOTATION_ROW_BG = "color-mix(in srgb, var(--color-accent-interactive) 8%, transparent)"

const isExpectedValidationSaveFailure = (error: unknown): boolean => {
	const message = error instanceof Error ? error.message : String(error)
	return message.startsWith("Save failed:")
}

interface TagFilterPopoverProps {
	filters: EventReviewConfig
	setFilters: SetterOrUpdater<EventReviewConfig>
	availableTags: string[]
}

const TagFilterPopover = ({ filters, setFilters, availableTags }: TagFilterPopoverProps) => {
	const [includeInput, setIncludeInput] = useState("")
	const [excludeInput, setExcludeInput] = useState("")
	const [activeField, setActiveField] = useState<"include" | "exclude" | null>(null)

	const hasFilters = filters.includeTags.length > 0 || filters.excludeTags.length > 0

	const addTag = (field: "include" | "exclude", value: string) => {
		const trimmed = value.trim()
		if (!trimmed || trimmed.length > 25) return

		const key = field === "include" ? "includeTags" : "excludeTags"
		const existing = filters[key].map(t => t.toLowerCase())
		if (existing.includes(trimmed.toLowerCase())) return

		setFilters(prev => ({ ...prev, [key]: [...prev[key], trimmed] }))
		if (field === "include") setIncludeInput("")
		else setExcludeInput("")
	}

	const removeTag = (field: "include" | "exclude", tag: string) => {
		const key = field === "include" ? "includeTags" : "excludeTags"
		setFilters(prev => ({ ...prev, [key]: prev[key].filter(t => t !== tag) }))
	}

	const clearAll = () => {
		setFilters(() => ({ includeTags: [], excludeTags: [] }))
		setIncludeInput("")
		setExcludeInput("")
	}

	const currentInput = activeField === "include" ? includeInput : excludeInput
	const alreadyUsed = useMemo(() => new Set([
		...filters.includeTags.map(t => t.toLowerCase()),
		...filters.excludeTags.map(t => t.toLowerCase()),
	]), [filters.includeTags, filters.excludeTags])

	const suggestions = useMemo(() => {
		if (!activeField || !currentInput.trim()) return []
		const lower = currentInput.toLowerCase()
		return availableTags.filter(tag =>
			tag.toLowerCase().includes(lower) && !alreadyUsed.has(tag.toLowerCase())
		)
	}, [activeField, currentInput, availableTags, alreadyUsed])

	const renderSection = (
		label: string,
		field: "include" | "exclude",
		tags: string[],
		inputValue: string,
		setInputValue: (v: string) => void,
	) => (
		<MobergColumn gap="4px">
			<span style={{ fontSize: MobergFontSize.SMALL, fontWeight: 600 }}>
				{label}
			</span>
			<input
				value={inputValue}
				placeholder={`Type to ${field}...`}
				onChange={e => setInputValue(e.target.value)}
				onFocus={() => setActiveField(field)}
				onKeyDown={e => {
					if (e.key === "Enter") {
						addTag(field, inputValue)
						e.preventDefault()
					}
				}}
				onBlur={() => {
					if (inputValue.trim()) addTag(field, inputValue)
					setTimeout(() => setActiveField(null), 150)
				}}
				style={{
					padding: "4px 8px",
					borderRadius: "4px",
					border: "1px solid var(--color-border-default)",
					background: "var(--color-input-bg)",
					color: "var(--color-text-primary)",
					fontSize: MobergFontSize.SMALL,
					outline: "none",
					width: "100%",
					boxSizing: "border-box",
				}}
			/>
			{tags.length > 0 && (
				<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
					{tags.map(tag => (
						<span
							key={tag}
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "4px",
								borderRadius: "12px",
								padding: "2px 6px 2px 8px",
								backgroundColor: field === "include" ? "var(--color-blue-hover-bg, var(--color-hover-active-blue))" : "var(--color-hover-neutral)",
								border: "1px solid var(--color-border-default)",
								color: field === "include" ? "var(--color-accent-interactive)" : "var(--color-text-primary)",
								fontSize: MobergFontSize.SMALL,
							}}
						>
							{tag}
							<button
								type="button"
								aria-label={`Remove ${tag}`}
								onClick={() => removeTag(field, tag)}
								onMouseDown={e => e.stopPropagation()}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									padding: 0,
									border: "none",
									background: "transparent",
									color: "inherit",
									cursor: "pointer",
								}}
							>
								<MdCancel size={14} />
							</button>
						</span>
					))}
				</div>
			)}
		</MobergColumn>
	)

	return (
		<MobergColumn gap="12px" style={{ width: "240px", padding: "4px" }}>
			{renderSection("Include tags", "include", filters.includeTags, includeInput, setIncludeInput)}
			{renderSection("Exclude tags", "exclude", filters.excludeTags, excludeInput, setExcludeInput)}

			{suggestions.length > 0 && (
				<div style={{
					maxHeight: "120px",
					overflowY: "auto",
					border: "1px solid var(--color-border-default)",
					borderRadius: "4px",
				}}>
					{suggestions.map(tag => (
						<div
							key={tag}
							onMouseDown={e => {
								e.preventDefault()
								if (activeField) addTag(activeField, tag)
							}}
							style={{
								padding: "4px 8px",
								cursor: "pointer",
								fontSize: MobergFontSize.SMALL,
							}}
						>
							{tag}
						</div>
					))}
				</div>
			)}

			{hasFilters && (
				<MobergButton
					onClick={clearAll}
					theme={MobergTheme.RED}
					variant={MobergButtonVariant.CLEAR}
					style={{ padding: TEXT_BUTTON_PADDING, alignSelf: "flex-start" }}
				>
					Clear all
				</MobergButton>
			)}
		</MobergColumn>
	)
}

const AnnotationControls = ({
	activeAnnotationSession,
	annotationGroupOpts,
	isEventReviewModeActive,
	sourceGroupName,
	handleChangeAnnotationGroup,
	handleToggleEventReviewMode,
}: {
	activeAnnotationSession: string | number | undefined
	annotationGroupOpts: MobergDropdownOption[]
	isEventReviewModeActive: boolean
	sourceGroupName: string | undefined
	handleToggleEventReviewMode: () => void
	handleChangeAnnotationGroup: (value: string | number) => void
}) => {
	const { createModal } = useModalProvider()
	const { environmentVariables } = useEnvironmentVariablesProvider()

	return (
		<MobergRow expand={false} horizontalAlign="space-between">
			<MobergRow expand={false} gap="4px" horizontalAlign="left">
				<MobergButton
					onClick={() => createModal(<AnnotationSessionManagerModal />)}
					shape={MobergButtonShape.SQUARE}
					tooltip={isEventReviewModeActive ? "Disabled during event review" : "Manage annotation sessions"}
					disabled={isEventReviewModeActive}
					style={{ padding: ICON_BUTTON_PADDING }}
					dataTestId="load-annotations-button"
				>
					<MdOutlineFolder size={MobergIconSize.REGULAR} />
				</MobergButton>

				{environmentVariables.FEATURE_FLAG_EVENT_REVIEW && (
					<MobergButton
						onClick={handleToggleEventReviewMode}
						shape={MobergButtonShape.SQUARE}
						theme={isEventReviewModeActive ? MobergTheme.BLUE : MobergTheme.BLACK}
						active={isEventReviewModeActive}
						style={{
							padding: ICON_BUTTON_PADDING,
						}}
						tooltip={isEventReviewModeActive ? "Disable event review mode" : "Enable event review mode"}
					>
						<MdRateReview size={MobergIconSize.REGULAR} />
					</MobergButton>
				)}
			</MobergRow>

			<MobergRow expand={false} horizontalAlign="right" gap="8px">
				{isEventReviewModeActive && sourceGroupName ? (
					<span style={{
						fontSize: MobergFontSize.REGULAR,
						color: "var(--color-text-primary)",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textAlign: "center",
						marginRight: "8px",
					}}>
						<strong>Reviewing:</strong> {sourceGroupName}
					</span>
				) : (
					<MobergDropdown
						label={"Annotation Group"}
						options={annotationGroupOpts}
						onChange={handleChangeAnnotationGroup}
						selectedValue={activeAnnotationSession}
						size={MobergDropdownSize.SMALL}
						width={150}
					/>
				)}
			</MobergRow>
		</MobergRow>
	)
}

const AnnotationsTable = ({
	columns,
	tableData,
	isEventReviewModeActive,
	currentAnnotationId,
	paginationModel,
	sortModel,
	searchValue,
	tableRef,
	filters,
	setFilters,
	availableTags,
	handleSelectedRow,
	handlePaginationModelChange,
	handleSortModelChange,
	setSearchValue,
}: {
	columns: AnnotationTableColumn[]
	tableData: AnnotationTableRow[]
	isEventReviewModeActive: boolean
	currentAnnotationId: number | undefined
	paginationModel: PaginationModel
	sortModel: TableSortModel
	searchValue: string
	tableRef: MutableRefObject<HTMLDivElement | null>
	filters: EventReviewConfig
	setFilters: SetterOrUpdater<EventReviewConfig>
	availableTags: string[]
	handleSelectedRow: (selected: AnnotationTableRow) => void
	handlePaginationModelChange: (model: PaginationModel) => void
	handleSortModelChange: (model: TableSortModel) => void
	setSearchValue: Dispatch<SetStateAction<string>>
}) => {
	const filterMenu = useMobergMenu({ placement: "bottom-start" })
	const hasActiveFilters = filters.includeTags.length > 0 || filters.excludeTags.length > 0

	const filterButton = (
		<>
			<MobergButton
				onClick={filterMenu.open}
				shape={MobergButtonShape.SQUARE}
				theme={hasActiveFilters ? MobergTheme.BLUE : MobergTheme.BLACK}
				active={hasActiveFilters}
				style={{
					padding: ICON_BUTTON_PADDING,
				}}
				tooltip="Filter annotations by tag"
			>
				<MdFilterList size={MobergIconSize.REGULAR} />
			</MobergButton>
			<filterMenu.MobergMenu style={{ marginTop: "4px" }}>
				<TagFilterPopover filters={filters} setFilters={setFilters} availableTags={availableTags} />
			</filterMenu.MobergMenu>
		</>
	)

	return (
		<div style={{ position: "relative", flex: 1, minHeight: 0, width: "100%", overflow: "hidden" }}>
			<ModernMUITable
				tableData={tableData}
				columns={columns}
				paginationModel={paginationModel}
				sortModel={sortModel}
				searchValue={searchValue}
				onPaginationModelChange={handlePaginationModelChange}
				onSortModelChange={handleSortModelChange}
				checkboxSelection={false}
				isRowSelectable={() => false}
				disableRowSelectionOnClick={true}
				handleOnRowClick={handleSelectedRow}
				onSearchValueChanged={(searchValue: string) => setSearchValue(searchValue)}
				tableToolbarProps={{
					searchInput: { isShow: true },
					columnButton: { isShow: false },
					startAdornment: filterButton,
				}}
				height="100%"
				scrollbarAlwaysVisible={true}
				disableVirtualization={false}
				tableRef={tableRef}
				getRowClassName={(params: any) => (isEventReviewModeActive && currentAnnotationId === params.id ? "current-annotation-row" : "")}
				overrideStyles={{
					// Ensure the grid fills its container vertically
					"& .MuiDataGrid-root": {
						display: "flex",
						flexDirection: "column",
						height: "100%",
						overflow: "auto",
					},
					"& .MuiDataGrid-main": {
						flex: 1,
						minHeight: "0 !important",
					},
					"& .MuiDataGrid-row.current-annotation-row": {
						backgroundColor: `${CURRENT_ANNOTATION_ROW_BG} !important`,
					},
					"& .MuiDataGrid-row.current-annotation-row .MuiDataGrid-cell": {
						backgroundColor: `${CURRENT_ANNOTATION_ROW_BG} !important`,
					},
					"& .MuiDataGrid-row": {
						lineHeight: "40px",
						minHeight: "40px !important",
					},
					"& .MuiDataGrid-cell": {
						minHeight: "40px !important",
						padding: "4px 8px",
						overflow: "hidden",
					},
					"& .MuiDataGrid-columnHeaders": {
						height: "40px !important",
					},
					"& .MuiDataGrid-footerContainer": {
						height: "52px",
					},
					// Clamp pagination label margins so no extra whitespace appears
					"& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel": {
						marginTop: "0 !important",
						marginBottom: "0 !important",
					},
					// Force vertical scrollbar to show, hide horizontal
					"& .MuiDataGrid-virtualScroller": {
						overflowX: "hidden !important",
						overflowY: "auto !important",
						scrollbarWidth: "thin",
						scrollbarColor: "var(--color-scrollbar-thumb) var(--color-scrollbar-track)",
					},
					"& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
						width: "8px",
					},
					"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
						background: "var(--color-scrollbar-track)",
						borderRadius: "4px",
					},
					"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
						background: "var(--color-scrollbar-thumb)",
						borderRadius: "4px",
					},
					"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
						background: "var(--color-scrollbar-thumb)",
					},
					"& .MuiDataGrid-virtualScrollerContent": {
						overflow: "visible",
					},
					// Hide horizontal scrollbar
					"& .MuiDataGrid-virtualScrollerRenderZone": {
						overflow: "hidden",
					},
					// Enable column resizing
					"& .MuiDataGrid-columnSeparator": {
						display: "block",
					},
					"& .MuiDataGrid-columnHeader": {
						overflow: "visible",
					},
					"& .MuiDataGrid-columnHeaderResizeZone": {
						display: "block",
					},
				}}
			/>
		</div>
	)
}

const TextCell = ({
	text,
	style,
	...rest
}: {
	text: string
	style?: React.CSSProperties | undefined
} & React.HTMLAttributes<HTMLSpanElement>) => {
	return (
		<span
			title={text}
			style={{
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
				fontSize: MobergFontSize.REGULAR,
				...style,
			}}
			{...rest}
		>
			{text}
		</span>
	)
}

const EventReviewControls = ({
	currentAnnotation,
	isPlaying,
	canPlay,
	validationError,
	saveReviewTask,
	incompleteEvents,
	totalEvents,
	togglePlaying,
	jumpToCurrentEvent,
	goToNextAnnotation,
	goToPreviousAnnotation,
}: {
	currentAnnotation: Annotation | undefined
	isPlaying: boolean
	canPlay: boolean
	validationError: string | undefined
	saveReviewTask: AsyncTask
	incompleteEvents: number
	totalEvents: number
	togglePlaying: () => void
	jumpToCurrentEvent: () => void
	goToNextAnnotation: () => void
	goToPreviousAnnotation: () => void
}) => {
	const nonValidationSaveError = saveReviewTask.error && !isExpectedValidationSaveFailure(saveReviewTask.error)
		? saveReviewTask.error
		: undefined
	const actionError = validationError ?? nonValidationSaveError

	return (
		<MobergColumn
			style={{
				flex: "0 0 35%",
				marginTop: "8px",
				backgroundColor: "var(--color-surface-panel)",
				border: "1px solid var(--color-border-default)",
				borderRadius: "4px",
				overflow: "hidden",
			}}
		>
			<MobergRow
				expand={false}
				horizontalAlign="space-between"
				verticalAlign="center"
				style={{
					padding: "6px 16px",
					borderBottom: "1px solid var(--color-border-default)",
				}}
			>
				<MobergRow gap="8px" verticalAlign="center">
					<MobergButton
						onClick={jumpToCurrentEvent}
						theme={MobergTheme.BLUE}
						variant={MobergButtonVariant.CLEAR}
						disabled={!currentAnnotation || isPlaying}
						tooltip="Jump to current"
						style={{
							padding: TEXT_BUTTON_PADDING,
						}}
					>
						Jump to current
					</MobergButton>

					<MobergButton
						onClick={togglePlaying}
						theme={MobergTheme.BLUE}
						disabled={!isPlaying && !canPlay}
						tooltip={isPlaying ? "Stop annotation playthrough" : "Play through event"}
						shape={MobergButtonShape.CIRCLE}
						style={{ padding: ICON_BUTTON_PADDING }}
					>
						{isPlaying ? <MdPauseCircleOutline size={MobergIconSize.REGULAR} /> : <MdPlayCircleOutline size={MobergIconSize.REGULAR} />}
					</MobergButton>

						{saveReviewTask.isWaiting && (
							<CircularProgress variant="indeterminate" size={16} thickness={3} sx={{ marginLeft: "4px", color: "var(--color-accent-interactive)" }} />
						)}

						{/* TODO: move this into the form */}
						{actionError && (
							<MdOutlineErrorOutline size={MobergIconSize.REGULAR} title={actionError} color={getColors(MobergTheme.RED).main} />
						)}
					</MobergRow>

				<MobergRow gap="4px" verticalAlign="center" horizontalAlign="right" style={{ marginLeft: "auto" }}>
					<MobergButton
						onClick={goToPreviousAnnotation}
						theme={MobergTheme.MEDIUM_GRAY}
						variant={MobergButtonVariant.CLEAR}
						disabled={isPlaying || saveReviewTask.isWaiting || validationError !== undefined}
						tooltip="Previous event"
						style={{
							padding: TEXT_BUTTON_PADDING,
						}}
					>
						<MdArrowBack size={MobergIconSize.SMALL} />
						Prev
					</MobergButton>

					<MobergButton
						onClick={goToNextAnnotation}
						theme={MobergTheme.BLUE}
						variant={MobergButtonVariant.CLEAR}
						disabled={isPlaying || saveReviewTask.isWaiting || validationError !== undefined}
						tooltip="Next event"
						style={{
							padding: TEXT_BUTTON_PADDING,
						}}
					>
						Next
						<MdArrowForward size={MobergIconSize.SMALL} />
					</MobergButton>
				</MobergRow>
			</MobergRow>

			<div
				style={{
					flex: 1,
					height: "100%",
					overflow: "hidden",
					padding: "0px 16px",
				}}
			>
				<EventReviewFormsWindow incompleteEvents={incompleteEvents} totalEvents={totalEvents} />
			</div>
		</MobergColumn>
	)
}

function AnnotationsBar(props: AnnotationsBarProps) {
	const { enableSidebar, setEnableSidebar } = props
	const { createModal } = useModalProvider()
	const patientInfo = useRecoilValue(currentPatientFileInfoAtom)
	const annotationGroups = useRecoilValue(annotationGroupsAtom)
	const [searchValue, setSearchValue] = useState("")
	const cleanSearchValue = searchValue.trim().toLowerCase()
	const [activeAnnotationSession, setActiveAnnotationSession] = useRecoilState(activeAnnotationSessionAtom)
	const sidebarContentRef = useRef<HTMLDivElement>(null)
	const eventReview = useEventReview()
	const eventReviewForm = useEventReviewForm()
	const {
		firstError,
		goToNextAnnotation,
		goToPreviousAnnotation,
		navigateToAnnotation,
		saveReviewTask,
	} = eventReviewForm
	const isEventReviewModeActive = eventReview.isActive
	const playThroughEvent = usePlayThroughAnnotation()

	useEffect(() => {
		if (isEventReviewModeActive) {
			setEnableSidebar(true)
		}
	}, [isEventReviewModeActive, setEnableSidebar])

	const filteredAnnotations = useMemo(() => {
		return eventReview.sortedAnnotations.filter(
			(annotation: Annotation) =>
				annotation.text.toLowerCase().includes(cleanSearchValue) ||
				annotation.description.toLowerCase().includes(cleanSearchValue) ||
				annotation.tags.some(tag => tag.toLowerCase().includes(cleanSearchValue)),
		)
	}, [cleanSearchValue, eventReview.sortedAnnotations])

	const annotationTableData = useMemo<AnnotationTableRow[]>(() => {
		const reviewedIds = eventReview.reviewedEventIds
		return filteredAnnotations.map(annotation => ({
			id: annotation.id,
			name: annotation.text,
			time: annotation.start_time,
			duration: annotation.end_time - annotation.start_time,
			complete: reviewedIds.has(annotation.id),
			isCurrent: isEventReviewModeActive && eventReview.currentAnnotation?.id === annotation.id,
		}))
	}, [eventReview.currentAnnotation?.id, eventReview.reviewedEventIds, filteredAnnotations, isEventReviewModeActive])

	const [paginationModel, setPaginationModel] = useState<PaginationModel>({ page: 0, pageSize: 25 })
	const [sortModel, setSortModel] = useState<TableSortModel>([])
	const annotationTableRef = useRef<HTMLDivElement | null>(null)
	const shouldAutoFocusCurrentAnnotationRef = useRef(false)
	const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
		setPaginationModel(model)
	}

	const requestAutoFocusCurrentAnnotation = useCallback(() => {
		shouldAutoFocusCurrentAnnotationRef.current = true
	}, [])

	const resetTableStateForEventNavigation = useCallback(() => {
		requestAutoFocusCurrentAnnotation()
		setSearchValue(previous => (previous === "" ? previous : ""))
		setSortModel(previous => (previous.length === 0 ? previous : []))
	}, [requestAutoFocusCurrentAnnotation])

	const writableAnnotationGroups = annotationGroups.filter(group => !group.read_only)
	const annotationGroupOpts: MobergDropdownOption[] = writableAnnotationGroups.map(group => ({
		label: group.group_name,
		value: group.id,
		highlight: group.id === activeAnnotationSession?.id,
	}))

	const handleChangeAnnotationGroup = (value: string | number) => {
		const selectedGroup = writableAnnotationGroups.find(group => group.id === value)
		if (selectedGroup) {
			setActiveAnnotationSession(selectedGroup)
		}
	}

	const handleToggleEventReviewMode = () => {
		if (!isEventReviewModeActive) {
			createModal(<AnnotationGroupLoader style={{ height: "80vh" }} defaultMode="event-review" />)
		} else {
			// Trying to disable event review mode
			createModal(
				<ConfirmationModal
					title="Disable Event Review Mode"
					description="Are you sure you want to exit event review mode?"
					confirmButton={{
						text: "Disable",
						theme: MobergTheme.RED,
						onClick: () => {
							saveReviewTask.run()
								.catch(err => {
									if (isExpectedValidationSaveFailure(err)) {
										return
									}
									console.error(err)
								})
								.finally(() => {
									eventReview.exitEventReview()
								})
						},
					}}
				/>,
			)
		}
	}

	const jumpToCurrentEvent = useCallback(() => {
		if (playThroughEvent.isPlaying) {
			return
		}

		resetTableStateForEventNavigation()
		if (eventReview.currentAnnotation) {
			const noSave = true
			navigateToAnnotation(eventReview.currentAnnotation, noSave)
		}
	}, [eventReview.currentAnnotation, navigateToAnnotation, playThroughEvent.isPlaying, resetTableStateForEventNavigation])

	const handleGoToNextAnnotation = useCallback(() => {
		if (playThroughEvent.isPlaying || saveReviewTask.isWaiting || firstError !== undefined) {
			return
		}

		resetTableStateForEventNavigation()
		goToNextAnnotation()
	}, [firstError, goToNextAnnotation, playThroughEvent.isPlaying, resetTableStateForEventNavigation, saveReviewTask.isWaiting])

	const handleGoToPreviousAnnotation = useCallback(() => {
		if (playThroughEvent.isPlaying || saveReviewTask.isWaiting || firstError !== undefined) {
			return
		}

		resetTableStateForEventNavigation()
		goToPreviousAnnotation()
	}, [firstError, goToPreviousAnnotation, playThroughEvent.isPlaying, resetTableStateForEventNavigation, saveReviewTask.isWaiting])

	useEventReviewHotKeys({
		isActive: eventReview.isActive,
		onNext: handleGoToNextAnnotation,
		onPrevious: handleGoToPreviousAnnotation,
		onPlayThrough: playThroughEvent.toggle,
		onJumpToCurrent: jumpToCurrentEvent,
	})

	useLayoutEffect(() => {
		const sidebarContent = sidebarContentRef.current
		if (!sidebarContent) {
			return
		}

		if (!enableSidebar) {
			sidebarContent.style.width = "0px"
			sidebarContent.style.transition = `all ${MobergAnimationCurve.EASE_OUT} ${MobergAnimationTiming.REGULAR}`
		} else {
			sidebarContent.style.width = eventReview.isActive ? "460px" : "400px"
			sidebarContent.style.transition = `all ${MobergAnimationCurve.EASE_IN} ${MobergAnimationTiming.REGULAR}`
		}
	}, [enableSidebar, eventReview.isActive])

	const handleSelectedRow = useCallback(
		(selected: AnnotationTableRow) => {
			if (playThroughEvent.isPlaying) {
				return
			}

			const annotation = eventReview.sortedAnnotations.find(a => a.id === selected.id)

			if (!annotation) {
				return
			}

			if (isEventReviewModeActive) {
				navigateToAnnotation(annotation)
			} else {
				jumpAllWindows(new Date(annotation.end_time))
			}
		},
		[eventReview.sortedAnnotations, isEventReviewModeActive, navigateToAnnotation, playThroughEvent.isPlaying],
	)

	const annotationTableColumns = useMemo(() => {
		const baseColumns = [
			{
				field: "time",
				width: 110,
				headerName: "Time",
				visible: true,
				minWidth: 100,
				maxWidth: 100,
				renderCell: (params: any) => {
					const formattedDate = formatDate(new Date(params.row.time), {
						timeZone: patientInfo.timeZone,
						seconds: true,
						milliseconds: true,
						hourCycle: "h23",
					})
					const parts = formattedDate.split(", ")
					if (parts.length >= 3) {
						const [monthAndDay, year, time] = parts
						return (
							<div style={{ lineHeight: "1.6" }}>
								<TextCell text={`${monthAndDay}, ${year}`} title={formattedDate} style={{ fontSize: MobergFontSize.SMALL, display: "block" }} />
								<TextCell text={time} title={formattedDate} style={{ fontSize: MobergFontSize.SMALL, display: "block" }} />
							</div>
						)
					} else {
						return <TextCell text={formattedDate} title={formattedDate} style={{ fontSize: MobergFontSize.SMALL }} />
					}
				},
			},
			{
				field: "name",
				width: 130,
				headerName: "Name",
				visible: true,
				minWidth: 120,
				maxWidth: 150,
				renderCell: (params: any) => {
					return (
						<TextCell
							text={params.row.name}
							style={{
								color: "var(--color-accent-interactive)",
								fontWeight: "bold",
							}}
							onMouseDown={() => handleSelectedRow(params.row)}
						/>
					)
				},
			},
			{
				field: "duration",
				width: 110,
				headerName: "Duration",
				visible: true,
				minWidth: 100,
				maxWidth: 120,
				renderCell: (params: any) => {
					const duration = fromMilliseconds(params.row.duration, [DurationUnit.SECONDS, DurationUnit.MINUTES])
					return <TextCell text={getFormattedDuration(duration, { abbreviation: true })} />
				},
			},
		]

		if (isEventReviewModeActive) {
			baseColumns.push({
				field: "complete",
				width: 110,
				headerName: "Status",
				visible: true,
				minWidth: 100,
				maxWidth: 130,
				renderCell: (params: any) => {
					const isComplete = params.row.complete
					const colors = isComplete ? STATUS_COLORS.complete : STATUS_COLORS.incomplete

					return (
						<span
							style={{
								display: "inline",
								padding: "2px 8px",
								borderRadius: "8px",
								fontSize: MobergFontSize.SMALL,
								fontWeight: 600,
								whiteSpace: "nowrap",
								backgroundColor: colors.background,
								color: colors.text,
							}}
						>
							{isComplete ? "Complete" : "Incomplete"}
						</span>
					)
				},
			})
		}

		return baseColumns
	}, [handleSelectedRow, isEventReviewModeActive, patientInfo.timeZone])

	// Update pagination model when searching
	useEffect(() => {
		setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }))
	}, [cleanSearchValue])

	useEffect(() => {
		shouldAutoFocusCurrentAnnotationRef.current = isEventReviewModeActive
	}, [isEventReviewModeActive])

	// scroll to current event
	useEffect(() => {
		if (!isEventReviewModeActive || !shouldAutoFocusCurrentAnnotationRef.current) {
			return
		}

		const currentAnnotationId = eventReview.currentAnnotation?.id

		if (!currentAnnotationId) {
			shouldAutoFocusCurrentAnnotationRef.current = false
			return
		}

		const rowIndex = filteredAnnotations.findIndex(annotation => annotation.id === currentAnnotationId)

		if (rowIndex === -1) {
			shouldAutoFocusCurrentAnnotationRef.current = false
			return
		}

		const targetPage = Math.floor(rowIndex / paginationModel.pageSize)

		if (paginationModel.page !== targetPage) {
			setPaginationModel(prev => ({ ...prev, page: targetPage }))
			return
		}

		const scrollToCurrentRow = () => {
			const gridElement = annotationTableRef.current

			if (!gridElement) {
				return false
			}

			const virtualScroller = gridElement.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null

			if (!virtualScroller) {
				const rowElement = gridElement.querySelector(`[data-id="${currentAnnotationId}"]`) as HTMLElement | null
				if (!rowElement) {
					return false
				}
				rowElement.scrollIntoView({ block: "nearest", inline: "nearest" })
				return true
			}

			const rowElement = gridElement.querySelector(`[data-id="${currentAnnotationId}"]`) as HTMLElement | null
			const fallbackRowElement = gridElement.querySelector(".MuiDataGrid-row") as HTMLElement | null
			const rowHeight = rowElement?.getBoundingClientRect().height
				?? fallbackRowElement?.getBoundingClientRect().height
				?? 40
			const rowIndexOnPage = rowIndex - paginationModel.page * paginationModel.pageSize
			const targetScrollTop = Math.max(
				0,
				rowIndexOnPage * rowHeight - (virtualScroller.clientHeight - rowHeight) / 2,
			)

			virtualScroller.scrollTop = targetScrollTop
			return true
		}

		requestAnimationFrame(() => {
			if (scrollToCurrentRow()) {
				shouldAutoFocusCurrentAnnotationRef.current = false
				return
			}

			requestAnimationFrame(() => {
				scrollToCurrentRow()
				shouldAutoFocusCurrentAnnotationRef.current = false
			})
		})
	}, [eventReview.currentAnnotation?.id, filteredAnnotations, isEventReviewModeActive, paginationModel.page, paginationModel.pageSize])

	return (
		<div
			style={{
				backgroundColor: "var(--color-surface-panel)",
				boxShadow: MobergBoxShadow.LOW,
				borderRadius: "6px 0px 0px 6px",
				position: "relative",
				margin: "4px 0px",
				padding: "8px 2px"
			}}
		>
			<MobergRow expand={false} style={{ height: "100%" }} gap="4px">
				<MobergColumn verticalAlign="center">
					<MobergButton onClick={() => setEnableSidebar(previous => !previous)} shape={MobergButtonShape.SQUARE} style={{ padding: "0" }}>
						{enableSidebar ? <MdChevronRight size={MobergIconSize.REGULAR} /> : <MdChevronLeft size={MobergIconSize.REGULAR} />}
					</MobergButton>
				</MobergColumn>

				<div
					ref={sidebarContentRef}
					style={{
						height: "100%",
						overflow: "hidden",
						transition: `all ${MobergAnimationTiming.REGULAR} ${MobergAnimationCurve.EASE_OUT}`,
					}}
				>
					<MobergColumn expand={true} gap="8px" style={{ padding: "0px 8px 8px 0px" }}>
						<AnnotationControls
							activeAnnotationSession={activeAnnotationSession?.id}
							annotationGroupOpts={annotationGroupOpts}
							isEventReviewModeActive={isEventReviewModeActive}
							sourceGroupName={eventReview.sourceGroup?.group_name}
							handleChangeAnnotationGroup={handleChangeAnnotationGroup}
							handleToggleEventReviewMode={handleToggleEventReviewMode}
						/>

						<AnnotationsTable
							columns={annotationTableColumns}
							tableData={annotationTableData}
							isEventReviewModeActive={isEventReviewModeActive}
							currentAnnotationId={eventReview.currentAnnotation?.id}
							paginationModel={paginationModel}
							sortModel={sortModel}
							searchValue={searchValue}
							tableRef={annotationTableRef}
							filters={eventReview.filters}
							setFilters={eventReview.setFilters}
							availableTags={eventReview.availableTags}
							handleSelectedRow={handleSelectedRow}
							handlePaginationModelChange={handlePaginationModelChange}
							handleSortModelChange={setSortModel}
							setSearchValue={setSearchValue}
						/>

						{isEventReviewModeActive && (
							<EventReviewControls
								currentAnnotation={eventReview.currentAnnotation}
								isPlaying={playThroughEvent.isPlaying}
								canPlay={playThroughEvent.canPlay}
								validationError={firstError}
								saveReviewTask={saveReviewTask}
								incompleteEvents={eventReview.incompleteEventCount}
								totalEvents={eventReview.totalEvents}
								togglePlaying={playThroughEvent.toggle}
								jumpToCurrentEvent={jumpToCurrentEvent}
								goToNextAnnotation={handleGoToNextAnnotation}
								goToPreviousAnnotation={handleGoToPreviousAnnotation}
							/>
						)}
					</MobergColumn>
				</div>
			</MobergRow>
		</div>
	)
}

export default AnnotationsBar
